import { Formik, Form } from 'formik';
import i18n from 'i18n';
import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultModalProps, withLocationStateModal } from 'utils/modals';
import * as Yup from 'yup';

import { Box, Button, Grid, Typography } from '@mui/material';

import { useUpdateTalentEmailMutation } from '@libs/graphql-types';
import { ConnectedTextField } from '@libs/ui/components/form/text-field';
import { DefaultModal } from '@libs/ui/components/modals';

import { ChangeEmailModalData, EditProfileModals } from './types';

interface ChangeEmailModalProps extends DefaultModalProps<ChangeEmailModalData> {
  onSuccess?: () => void;
}

const validationSchema = Yup.object({
  newEmail: Yup.string()
    .email(
      i18n.t('editProfile.changeEmail.invalidEmail', { ns: 'talentProfile' }),
    )
    .required(
      i18n.t('editProfile.changeEmail.emailRequired', { ns: 'talentProfile' }),
    ),
});

interface FormValues {
  newEmail: string;
}

const ChangeEmailModalComponent: React.FC<ChangeEmailModalProps> = ({
  close,
  isOpen,
  modalData,
  onSuccess,
}) => {
  const { t } = useTranslation('talentProfile');
  const { enqueueSnackbar } = useSnackbar();
  const [updateTalentEmail, { loading }] = useUpdateTalentEmailMutation({
    onCompleted: () => {
      enqueueSnackbar(
        t('editProfile.changeEmail.snackbarSuccess'),
        { variant: 'success' },
      );
      onSuccess?.();
      close();
    },
  });

  const initialValues: FormValues = {
    newEmail: modalData?.currentEmail || '',
  };

  const handleSubmit = (values: FormValues) => {
    updateTalentEmail({
      variables: {
        email: values.newEmail,
      },
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid, dirty, submitForm }) => {
        return (
          <DefaultModal
            open={isOpen}
            handleClose={close}
            title={t('editProfile.changeEmail.title')}
            actions={
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    color="error"
                    variant="contained"
                    type="submit"
                    onClick={submitForm}
                    size="large"
                    disabled={!isValid || !dirty || loading}
                  >
                    {loading
                      ? t('editProfile.changeEmail.sending')
                      : t('editProfile.changeEmail.sendVerification')}
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={close}
                    disabled={loading}
                  >
                    {t('editProfile.changeEmail.cancel')}
                  </Button>
                </Grid>
              </Grid>
            }
          >
            <Box p={3}>
              <Typography variant="body2" color="textSecondary" mb={3}>
                {t('editProfile.changeEmail.description')}
              </Typography>

              <Form>
                <Grid container spacing={3} sx={{ mb: 0 }}>
                  <Grid item xs={12}>
                    <ConnectedTextField
                      name="newEmail"
                      fullWidth
                      size="small"
                      variant="filled"
                      label={t('editProfile.changeEmail.newEmail')}
                      type="email"
                      placeholder={t('editProfile.changeEmail.placeholder')}
                    />
                  </Grid>
                </Grid>
              </Form>
            </Box>
          </DefaultModal>
        );
      }}
    </Formik>
  );
};

export const ChangeEmailModal = withLocationStateModal<ChangeEmailModalData>({
  id: EditProfileModals.CHANGE_EMAIL,
})(ChangeEmailModalComponent);
